import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { uploadMedia } from "@/services/media";
import { createReceipt, readReceipt, type Receipt } from "@/services/receiving";
import { camOk, isDesk } from "@/lib/device";
import CameraDialog from "@/components/CameraDialog";
import TabletWait from "@/components/TabletWait";
import { askTablet, type CameraRequest } from "@/services/cameraRequests";
import { ReadOnlyArea, useReadOnly } from "@/components/ReadOnly";

type Page = { file: File; url: string | null; pdf: boolean };
const isPdf = (f: File) => /pdf/i.test(f.type) || /\.pdf$/i.test(f.name);
const isPhoto = (f: File) => /^image\//i.test(f.type) || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name);
// His MAX_REC_FILES: at most 20 images on one supplier reception.
const MAX_FILES = 20;

// His "Réception des documents fournisseur": photograph each page (or choose photos / PDFs, or drop
// them), then one button reads them and checks them against the Delivery calendar. The pages go
// straight from this browser to S3; the reading happens on the server.
export default function NoteReader({ warehouseId, onStarted, purpose = "furniture", extra = {}, children }: {
  warehouseId: number | null; onStarted: (r: Receipt) => void;
  // furniture: client furniture (Réception) · articles: kit articles into article stock (Production).
  purpose?: "furniture" | "articles"; extra?: Record<string, unknown>; children?: React.ReactNode;
}) {
  // His supplier reception (articles): at most 20 images, each file not added named with its reason,
  // and a screenshot pasted with Ctrl+V lands in the card on screen.
  const his = purpose === "articles";
  // His Receiving on a PC (Z Furniture rphoto): the live webcam, several pages, "Finish and read", or
  // "📱 Use the tablet". The supplier reception of Production keeps its plain camera button.
  const live = purpose === "furniture" && isDesk() && camOk();
  // His viewer level: a read-only person sees the card greyed — Receiving's, or Production's for
  // its supplier reception — and nothing dropped or pasted is taken (the server refuses it anyway).
  const area = his ? "production" : "receiving";
  const ro = useReadOnly(area);
  const [camera, setCamera] = useState(false);
  const [tablet, setTablet] = useState<CameraRequest | null>(null);
  const { t } = useT();
  const [pages, setPages] = useState<Page[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const photo = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);

  const count = useRef(0);
  count.current = pages.length;
  const add = (list: FileList | File[] | null) => {
    const chosen = Array.from(list ?? []);
    if (!his) {
      const ok = chosen.filter((f) => isPdf(f) || isPhoto(f));
      setError(ok.length < chosen.length ? t("note.type") : null);
      setPages((p) => [...p, ...ok.map((file) => ({ file, pdf: isPdf(file), url: isPdf(file) ? null : URL.createObjectURL(file) }))]);
      return;
    }
    const skipped: string[] = [];
    const ok: File[] = [];
    chosen.forEach((f) => {
      const name = f.name || "capture";
      if (!isPdf(f) && !isPhoto(f)) skipped.push(t("note.badFormat", { f: name }));
      else if (count.current + ok.length >= MAX_FILES) skipped.push(t("note.maxImages", { f: name, n: MAX_FILES }));
      else ok.push(f);
    });
    setError(skipped.length ? t("note.notAdded", { list: skipped.join(" ; ") }) + (ok.length ? t("note.othersReady") : "") : null);
    setPages((p) => [...p, ...ok.map((file) => ({ file, pdf: isPdf(file), url: isPdf(file) ? null : URL.createObjectURL(file) }))]);
  };
  // His document-wide paste: an image or PDF on the clipboard goes into this card when it is on screen;
  // an unnamed screenshot is renamed capture-HHMMSS.
  const card = useRef<HTMLDivElement>(null);
  const addRef = useRef(add);
  addRef.current = add;
  useEffect(() => {
    if (!his) return;
    const onPaste = (e: ClipboardEvent) => {
      if (ro || !card.current || card.current.offsetParent === null) return;
      const files = Array.from(e.clipboardData?.items ?? []).filter((it) => it.kind === "file").map((it) => it.getAsFile())
        .filter((f): f is File => !!f && (/^image\//.test(f.type) || f.type === "application/pdf"));
      if (!files.length) return;
      e.preventDefault();
      const stamp = new Date().toTimeString().slice(0, 8).replace(/:/g, "");
      addRef.current(files.map((f, i) => (f.name && f.name !== "image.png" ? f
        : new File([f], `capture-${stamp}${i ? `-${i}` : ""}.${f.type.split("/")[1] || "png"}`, { type: f.type }))));
      card.current.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [his, ro]);
  const remove = (i: number) => setPages((p) => {
    const gone = p[i];
    if (gone?.url) URL.revokeObjectURL(gone.url);
    return p.filter((_, j) => j !== i);
  });

  const go = useMutation({
    // His `recRead` starts with REC.err="": a message about a file not taken goes once the pages are sent.
    onMutate: () => setError(null),
    mutationFn: async () => {
      const receipt = await createReceipt({
        warehouse_id: warehouseId, source_kind: pages.some((p) => p.pdf) ? "pdf" : "photo", purpose, ...extra,
      });
      // One page after the other, in the order they were taken: page 1 is page 1.
      for (const p of pages) await uploadMedia("supplier_receipt", receipt.id, p.file);
      return readReceipt(receipt.id);
    },
    onSuccess: (r) => {
      pages.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      setPages([]);
      onStarted(r);
    },
  });

  const n = pages.length;
  const readLabel = purpose === "articles" ? t(n > 1 ? "artrec.sendMany" : "artrec.sendOne", { n })
    : t(n > 1 ? "note.readMany" : "note.readOne", { n });
  return (
    <div ref={card} className="px-5 pb-3">
      <ReadOnlyArea module={area}>
        {pages.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-3">
            {pages.map((p, i) => (
              <div key={i} className="w-28 text-center text-xs text-slate-600">
                {p.url ? (
                  <img src={p.url} alt={t("note.page").replace("{n}", String(i + 1))}
                    className="h-36 w-28 rounded border border-slate-200 object-cover" />
                ) : (
                  <div className="flex h-36 w-28 flex-col items-center justify-center rounded border border-slate-200 bg-slate-50 text-2xl">
                    📄<small className="text-xs">PDF</small>
                  </div>
                )}
                <div className="mt-1 truncate">{p.pdf ? p.file.name : t("note.page").replace("{n}", String(i + 1))}</div>
                {!go.isPending && (
                  <button onClick={() => remove(i)} className="mt-0.5 text-xs text-slate-500 underline">{t("note.del")}</button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => (live ? setCamera(true) : photo.current?.click())} disabled={go.isPending}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">{t("note.photo")}</button>
          <button onClick={() => files.current?.click()} disabled={go.isPending}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40">{t("note.files")}</button>
          <input ref={photo} type="file" accept="image/*" capture="environment" multiple hidden
            onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
          <input ref={files} type="file" accept="image/*,application/pdf,.pdf,.heic,.heif" multiple hidden
            onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); if (!go.isPending && !ro) add(e.dataTransfer.files); }}
          className={`mt-2 rounded-lg border-2 border-dashed px-3 py-4 text-center text-xs text-slate-500 ${
            over ? "border-slate-700 bg-slate-100" : "border-slate-300"}`}>
          {t("note.drop")}
        </div>
        {camera && (
          <CameraDialog mode="rec" onClose={() => setCamera(false)}
            onPages={(files) => { setCamera(false); add(files); }}
            onFile={() => { setCamera(false); files.current?.click(); }}
            onTablet={() => {
              setCamera(false);
              void askTablet({ mode: "rec", label: t("st.recL") }).then(setTablet).catch((e: Error) => setError(e.message));
            }} />
        )}
        {tablet && <TabletWait request={tablet} onClose={() => setTablet(null)} />}
        {children}
        {pages.length > 0 && (
          <button onClick={() => go.mutate()} disabled={go.isPending}
            className="mt-3 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {go.isPending ? t("note.uploading") : readLabel}
          </button>
        )}
      </ReadOnlyArea>
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
      {go.error && <p className="mt-2 text-sm text-red-700">{(go.error as Error).message}</p>}
    </div>
  );
}
